import React from "react";
import { useTranslation } from "react-i18next";
import { formatArs, formatNumber, formatPct, formatSignedArs } from "@/domain/projection/format";
import type { BreakEvenAnalysis, ProjectionModel, SimulationResult } from "@/domain/projection/types";
import { ProjectionNumberField, ProjectionSlider } from "./ProjectionNumberField";

type SimulatorPanelProps = {
  model: ProjectionModel;
  result: SimulationResult;
  breakEven: BreakEvenAnalysis;
  onChange: (model: ProjectionModel) => void;
};

function Line({
  label,
  value,
  tone = "neutral",
}: {
  label: string;
  value: string;
  tone?: "neutral" | "good" | "bad" | "muted";
}) {
  const color =
    tone === "good"
      ? "text-teal-700 dark:text-teal-300"
      : tone === "bad"
        ? "text-rose-600 dark:text-rose-400"
        : tone === "muted"
          ? "text-slate-500"
          : "text-slate-800 dark:text-slate-100";
  return (
    <div className="flex items-center justify-between gap-3 py-1.5">
      <span className="text-sm text-slate-500 dark:text-slate-400">{label}</span>
      <span className={`text-sm font-semibold tabular-nums ${color}`}>{value}</span>
    </div>
  );
}

export const SimulatorPanel: React.FC<SimulatorPanelProps> = ({ model, result, breakEven, onChange }) => {
  const { t } = useTranslation();
  const positive = result.resultado >= 0;
  const pctAvanzado = model.adoption.pctAvanzado;
  const pctBase = Math.max(0, 1 - pctAvanzado);
  const baseClients = Math.max(0, result.clinics - result.avanzadoClients);
  const ticketBase = model.plans.basePrice;
  const ticketAvanzado = model.plans.avanzadoPrice;
  const basePlanMrr = baseClients * ticketBase;
  const avanzadoPlanMrr = result.avanzadoClients * ticketAvanzado;

  const setClinicCount = (clinicCount: number) => {
    const enabled = model.clinics.filter((clinic) => clinic.enabled);
    const avgProfessionals = enabled.length > 0
      ? enabled.reduce((sum, clinic) => sum + clinic.professionals, 0) / enabled.length
      : model.synthetic.avgProfessionals;
    const avgConsultorios = enabled.length > 0
      ? enabled.reduce((sum, clinic) => sum + clinic.consultorios, 0) / enabled.length
      : model.synthetic.avgConsultorios;
    onChange({
      ...model,
      portfolioMode: "synthetic",
      synthetic: {
        clinicCount,
        avgProfessionals: model.portfolioMode === "synthetic" ? model.synthetic.avgProfessionals : avgProfessionals,
        avgConsultorios: model.portfolioMode === "synthetic" ? model.synthetic.avgConsultorios : avgConsultorios,
      },
    });
  };

  return (
    <div className="space-y-4">
      <div className={`panel-card p-5 ${positive ? "border-teal-200 dark:border-teal-800" : "border-rose-200 dark:border-rose-800"}`}>
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          {t("pages.projection.simulator.monthlyBalance")}
        </p>
        <p className={`mt-1 text-3xl font-bold tabular-nums tracking-tight ${positive ? "text-teal-700 dark:text-teal-300" : "text-rose-600 dark:text-rose-400"}`}>
          {formatSignedArs(result.resultado)}
        </p>
        <p className="mt-2 text-sm text-slate-500">
          {t("pages.projection.simulator.balanceFormula", {
            income: formatArs(result.mrrTotal),
            fixed: formatArs(result.fixedTotal),
            variable: formatArs(result.variableCost),
          })}
          {!positive && breakEven.reachable
            ? t("pages.projection.simulator.breakEven", { count: formatNumber(breakEven.clinicsNeeded) })
            : ""}
        </p>
      </div>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,0.85fr)]">
        <div className="panel-card space-y-5 p-4">
          <div>
            <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100">
              {t("pages.projection.simulator.levers")}
            </h3>
            <p className="mt-0.5 text-xs text-slate-500">{t("pages.projection.simulator.leversHint")}</p>
          </div>

          <div className="grid gap-3 sm:grid-cols-3">
            <ProjectionNumberField
              label={t("pages.projection.simulator.clinics")}
              value={result.clinics}
              min={0}
              step={1}
              onChange={setClinicCount}
            />
            <ProjectionNumberField
              label={t("pages.projection.simulator.usd")}
              value={model.assumptions.usdArs}
              min={1}
              step={10}
              onChange={(usdArs) => onChange({ ...model, assumptions: { ...model.assumptions, usdArs } })}
            />
            <label className="flex items-end gap-2 pb-2 text-sm text-slate-700 dark:text-slate-200">
              <input
                type="checkbox"
                checked={model.adoption.includeDebt}
                onChange={(event) =>
                  onChange({ ...model, adoption: { ...model.adoption, includeDebt: event.target.checked } })
                }
              />
              {t("pages.projection.simulator.includeDebt")}
            </label>
          </div>

          <div>
            <p className="mb-2 text-xs font-medium text-slate-600 dark:text-slate-300">
              {t("pages.projection.simulator.planMix")}
            </p>
            <div className="mb-1 flex items-center justify-between gap-3 text-xs font-semibold tabular-nums">
              <span className="text-teal-700 dark:text-teal-300">
                {t("pages.projection.simulator.avanzadoPct", { pct: Math.round(pctAvanzado * 100) })}
              </span>
              <span className="text-slate-600 dark:text-slate-300">
                {t("pages.projection.simulator.basePct", { pct: Math.round(pctBase * 100) })}
              </span>
            </div>
            <input
              type="range"
              min={0}
              max={100}
              step={1}
              value={Math.round(pctAvanzado * 100)}
              onChange={(event) =>
                onChange({
                  ...model,
                  adoption: { ...model.adoption, pctAvanzado: Number(event.target.value) / 100 },
                })
              }
              className="h-2 w-full cursor-pointer appearance-none rounded-full bg-slate-200 accent-teal-600 dark:bg-gray-700"
            />
            <div className="mt-1 flex items-center justify-between gap-3 text-[11px] text-slate-500">
              <span>
                {t("pages.projection.simulator.avanzadoCount", {
                  count: formatNumber(result.avanzadoClients),
                  ticket: formatArs(ticketAvanzado),
                  amount: formatArs(avanzadoPlanMrr),
                })}
              </span>
              <span>
                {t("pages.projection.simulator.baseCount", {
                  count: formatNumber(baseClients),
                  ticket: formatArs(ticketBase),
                  amount: formatArs(basePlanMrr),
                })}
              </span>
            </div>
            <p className="mt-1 text-[11px] text-slate-400">{t("pages.projection.simulator.planMixHint")}</p>
          </div>

          <ProjectionSlider
            label={t("pages.projection.simulator.conexion")}
            value={model.adoption.pctConexion}
            hint={t("pages.projection.simulator.conexionHint", {
              count: formatNumber(result.conexionClients),
              ticket: formatArs(result.ticketConexion),
            })}
            onChange={(pctConexion) => onChange({ ...model, adoption: { ...model.adoption, pctConexion } })}
          />
          <ProjectionSlider
            label={t("pages.projection.simulator.marketing")}
            value={model.adoption.pctMarketing}
            hint={t("pages.projection.simulator.marketingHint", {
              count: formatNumber(result.marketingClients),
              mrr: formatArs(result.marketingMrr),
            })}
            onChange={(pctMarketing) => onChange({ ...model, adoption: { ...model.adoption, pctMarketing } })}
          />
          <ProjectionSlider
            label={t("pages.projection.simulator.whatsapp")}
            value={model.adoption.whatsappUsageRate}
            hint={t("pages.projection.simulator.whatsappHint", { cost: formatArs(result.metaCost) })}
            onChange={(whatsappUsageRate) =>
              onChange({ ...model, adoption: { ...model.adoption, whatsappUsageRate } })
            }
          />

          {model.scenarios.length > 0 ? (
            <div>
              <p className="mb-2 text-xs font-medium text-slate-500">{t("pages.projection.simulator.presets")}</p>
              <div className="flex flex-wrap gap-2">
                {model.scenarios.map((scenario) => (
                  <button
                    key={scenario.id}
                    type="button"
                    className="rounded-full border border-slate-200 px-3 py-1 text-xs font-medium text-slate-700 hover:border-teal-400 hover:text-teal-800 dark:border-gray-600 dark:text-slate-200 dark:hover:border-teal-500"
                    onClick={() =>
                      onChange({
                        ...model,
                        adoption: {
                          ...scenario.adoption,
                          connectionMix: scenario.adoption.connectionMix.map((item) => ({ ...item })),
                          marketingMix: scenario.adoption.marketingMix.map((item) => ({ ...item })),
                        },
                      })
                    }
                  >
                    {scenario.name}
                  </button>
                ))}
              </div>
            </div>
          ) : null}
        </div>

        <div className="space-y-4">
          <div className="panel-card p-4">
            <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100">
              {t("pages.projection.simulator.pnlTitle")}
            </h3>
            <div className="mt-3 divide-y divide-slate-100 dark:divide-gray-700">
              <Line label={t("pages.projection.simulator.income")} value={formatArs(result.mrrTotal)} />
              <Line label={t("pages.projection.simulator.fixedCosts")} value={formatSignedArs(-result.fixedTotal)} tone="bad" />
              <Line label={t("pages.projection.simulator.variableCosts")} value={formatSignedArs(-result.variableCost)} tone="bad" />
              <Line
                label={t("pages.projection.simulator.balance")}
                value={formatSignedArs(result.resultado)}
                tone={positive ? "good" : "bad"}
              />
            </div>
            <p className="mt-3 text-[11px] leading-relaxed text-slate-400">
              {t("pages.projection.simulator.mrrHelp")}
            </p>
            <div className="mt-3 border-t border-slate-100 pt-3 dark:border-gray-700">
              <Line label={t("pages.projection.simulator.arpu")} value={formatArs(result.arpu)} tone="muted" />
              <Line label={t("pages.projection.simulator.perClinic")} value={formatSignedArs(result.resultadoPerClinic)} tone="muted" />
              <Line label={t("pages.projection.simulator.metaShare")} value={formatPct(result.metaOverMrr)} tone="muted" />
            </div>
          </div>

          <div className="panel-card p-4">
            <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100">
              {t("pages.projection.simulator.prices")}
            </h3>
            <p className="mt-0.5 text-xs text-slate-500">{t("pages.projection.simulator.pricesHint")}</p>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <ProjectionNumberField
                label={t("pages.projection.simulator.basePrice")}
                value={model.plans.basePrice}
                min={0}
                step={1000}
                onChange={(basePrice) => onChange({ ...model, plans: { ...model.plans, basePrice } })}
              />
              <ProjectionNumberField
                label={t("pages.projection.simulator.avanzadoPrice")}
                value={model.plans.avanzadoPrice}
                min={0}
                step={1000}
                onChange={(avanzadoPrice) => onChange({ ...model, plans: { ...model.plans, avanzadoPrice } })}
              />
              <ProjectionNumberField
                label={t("pages.projection.simulator.extraProfessional")}
                value={model.plans.extraProfessional}
                min={0}
                step={1000}
                onChange={(extraProfessional) => onChange({ ...model, plans: { ...model.plans, extraProfessional } })}
              />
              <ProjectionNumberField
                label={t("pages.projection.simulator.extraConsultorio")}
                value={model.plans.extraConsultorio}
                min={0}
                step={1000}
                onChange={(extraConsultorio) => onChange({ ...model, plans: { ...model.plans, extraConsultorio } })}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
